import { Button, Popconfirm, Table, Tooltip, Typography } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useState } from 'react'
import VSpace from '../../common/VSpace'
import {
  useDeleteSegmentMutation,
  useFeatureFlagSegmentsQuery,
  useFeatureFlagsQuery,
} from './featureFlagsQueries'
import { Segment } from '../../../domain/features/featureFlags'
import { flagIdsUsingSegment } from './featureFlagsModel'
import SegmentFormModal from './SegmentFormModal'

type EditorState = {
  open: boolean
  editing: Segment | null
}

const SegmentsPanel = () => {
  const { data: segments, isLoading } = useFeatureFlagSegmentsQuery()
  const { data: featuresData } = useFeatureFlagsQuery()
  const deleteMutation = useDeleteSegmentMutation()
  const [editorState, setEditorState] = useState<EditorState>({ open: false, editing: null })

  const features = featuresData?.features

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a: Segment, b: Segment) => a.name.localeCompare(b.name),
      render: (name: string, segment: Segment) => (
        <Typography.Link onClick={() => setEditorState({ open: true, editing: segment })}>
          {name}
        </Typography.Link>
      ),
    },
    {
      title: 'Tenants',
      dataIndex: 'tenantIds',
      render: (tenantIds: number[]) => tenantIds.length,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, segment: Segment) => {
        const usedByFlagIds = features ? flagIdsUsingSegment(segment.id, features) : null
        return (
          <>
            <Button
              type="link"
              size="small"
              onClick={() => setEditorState({ open: true, editing: segment })}
            >
              Edit
            </Button>
            {usedByFlagIds === null ? (
              <Tooltip title="Checking which feature flags use this segment…">
                <Button type="link" size="small" danger disabled>
                  Delete
                </Button>
              </Tooltip>
            ) : usedByFlagIds.length > 0 ? (
              <Tooltip
                title={`This segment is in use by ${usedByFlagIds.map((id) => `'${id}'`).join(', ')}. Remove it from those flags' rules first.`}
              >
                <Button type="link" size="small" danger disabled>
                  Delete
                </Button>
              </Tooltip>
            ) : (
              <Popconfirm
                title={`Permanently delete segment '${segment.name}'?`}
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => deleteMutation.mutate(segment.id)}
              >
                <Button type="link" size="small" danger>
                  Delete
                </Button>
              </Popconfirm>
            )}
          </>
        )
      },
    },
  ]

  return (
    <VSpace>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setEditorState({ open: true, editing: null })}
      >
        New Segment
      </Button>
      <Table<Segment>
        dataSource={segments}
        loading={isLoading}
        rowKey="id"
        columns={columns}
        pagination={false}
      />
      <SegmentFormModal
        open={editorState.open}
        editing={editorState.editing}
        onClose={() => setEditorState({ open: false, editing: null })}
      />
    </VSpace>
  )
}

export default SegmentsPanel
